import React, { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import CloseIcon from '@mui/icons-material/Close'
import { Box, IconButton, Typography } from '@mui/material'
import withStyles from '@mui/styles/withStyles'

import BlackTooltip from 'components/common/tooltips'
import AcquiringAdditionalCards from 'components/core/Acquiring/AcquiringAdditionalCards/AcquiringAdditionalCards'
import { chargeDetailsTableValues } from 'components/core/Acquiring/AcquiringState'
import { ErrorAlert } from 'components/core/Alert'
import { Spinner } from 'components/core/Progress/Progress-component'
import { TableFrame, TableSplitView } from 'components/core/Tables'
import { finishAcquiringChargesDownload, startAcquiringChargesDownload, startChargesReceiptPdf } from 'redux/acquiring/acquiring-actions'
import { useAppSelector } from 'redux/hooks'
import { IMAGES_PATH, themeStyles } from 'themes'
import combineStyles from 'utils/combineStyles'
import { dateAndTimeToLocaleString } from 'utils/helperNumbersFormating'

import { SmallCircle, Status } from './styles'

const pdfOval = IMAGES_PATH('/downloadOval.svg')
const chargesOval = IMAGES_PATH('/upload-documents.svg')

interface IProps {
    charge: ChargesRecord
    handleClose: () => void
    classes: Record<string, string>
}

const statusMark = {
    Initiated: <SmallCircle color="Finished" />,
    Charged: <SmallCircle color="Finished" />,
    Exchanged: <SmallCircle color="Finished" />,
    Withdrawn: <SmallCircle color="Finished" />,
    Refunded: <SmallCircle color="Canceled" />,
    ChargeBacked: <SmallCircle color="Canceled" />,
    Declined: <SmallCircle color="Canceled" />,
    Abandoned: <SmallCircle color="Canceled" />,
}

const AcquiringDashboardView: React.FC<IProps> = ({ charge, classes, handleClose }) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { status, statusDates, tableValues, additionalData } = useMemo(() => chargeDetailsTableValues(charge), [charge])
    const { cardAcquiringChargesDownload: download, loader } = useAppSelector(state => state.acquiring)
    const dateRange = `${dateAndTimeToLocaleString(statusDates.Initiated)} ${
        statusDates[status] ? `🡢 ${dateAndTimeToLocaleString(statusDates[status])}` : ''
    }`

    const handleDownload = () => {
        dispatch(startAcquiringChargesDownload({ id: charge.id, type: 'pdf' }))
    }

    const handleDownloadReceipt = () => {
        dispatch(startChargesReceiptPdf({ id: charge.id }))
    }

    const Download = useCallback(
        file => {
            const element = document.createElement('a')

            element.href = URL.createObjectURL(file)
            element.download = `Payment details #${charge.id}.pdf`
            document.body.appendChild(element)
            element.click()
            dispatch(finishAcquiringChargesDownload())
        },
        [dispatch, finishAcquiringChargesDownload]
    )

    useEffect(() => {
        if (download) {
            const file = new Blob([download], { type: 'application/pdf' })

            download && Download(file)
        }
    }, [download, Download])

    return (
        <>
            <div className={classes.modalFixedHeader}>
                <Typography data-sta-id="acquiring-modal-h1-title" variant="h1">
                    {t('Acquiring.ChargeDetails')} #{charge.id}
                </Typography>
                <IconButton
                    data-sta-id="acquiring-modal-button-close"
                    aria-label="close"
                    className={classes.modalCloseButton}
                    onClick={handleClose}
                    size="large"
                >
                    <CloseIcon />
                </IconButton>
            </div>
            <div className={classes.modalContent}>
                <ErrorAlert />
                <Status>
                    <Status>
                        {statusMark[status]} {t(`keys:chargeStatus.${status}`)}
                    </Status>{' '}
                    <span className="date">{dateRange}</span>
                </Status>
                <br />
                <AcquiringAdditionalCards
                    currencyCode={additionalData.currencyCode}
                    leftHeader={additionalData.leftSide.name}
                    leftValue={additionalData.leftSide.value}
                    rightHeader={additionalData.rightSide.name}
                    rightValue={additionalData.rightSide.value}
                    onlyLeft
                />
                <TableFrame>
                    <TableSplitView height="70" data={tableValues} />
                </TableFrame>
                <Box sx={{ display: 'flex', flexDirection: 'row', mt: 2 }}>
                    <Box sx={{ mr: 3 }}>
                        {loader?.ACQUIRING_CHARGES_DOWNLOAD ? (
                            <Spinner />
                        ) : (
                            <BlackTooltip data-clipboardtarget title={`${t('Acquiring.DetailsDownload')}`} placement="top" arrow={true}>
                                <img
                                    src={pdfOval}
                                    alt={`${t('Acquiring.DetailsDownload')}`}
                                    onClick={() => handleDownload()}
                                    style={{ cursor: 'pointer' }}
                                />
                            </BlackTooltip>
                        )}
                    </Box>
                    {charge.hasReceipt && (
                        <Box>
                            {loader?.CHARGES_RECEIPT_PDF ? (
                                <Spinner />
                            ) : (
                                <BlackTooltip data-clipboardtarget title={`${t('Acquiring.ReceiptDownload')}`} placement="top" arrow={true}>
                                    <img
                                        src={chargesOval}
                                        alt={`${t('Acquiring.ReceiptDownload')}`}
                                        onClick={() => handleDownloadReceipt()}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </BlackTooltip>
                            )}
                        </Box>
                    )}
                </Box>
            </div>
        </>
    )
}

export default withStyles(combineStyles(themeStyles.useModalStyles, themeStyles.useTableStyles))(AcquiringDashboardView)
